import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import AppScreenshotsGallery from '@/components/semantic/AppScreenshotsGallery';
import ArchitecturePreviewCard from '@/components/semantic/ArchitecturePreviewCard';
import BlueprintPreviewCard from '@/components/semantic/BlueprintPreviewCard';
import Breadcrumbs from '@/components/semantic/Breadcrumbs';
import FaqSection from '@/components/semantic/FaqSection';
import RelatedLinksSection from '@/components/semantic/RelatedLinksSection';
import SemanticSection from '@/components/semantic/SemanticSection';
import type { SemanticPage, SemanticResolvedLink } from '@/lib/semantic-content/types';

type BreadcrumbItem = {
  name: string;
  path: string;
};

type AppScreenshot = {
  id?: string;
  label?: string;
  imageUrl: string;
};

export default function SemanticPageLayout({
  page,
  breadcrumbItems,
  quickLinks,
  relatedLinks,
  screenshots = [],
}: {
  page: SemanticPage;
  breadcrumbItems: BreadcrumbItem[];
  quickLinks: SemanticResolvedLink[];
  relatedLinks: SemanticResolvedLink[];
  screenshots?: AppScreenshot[];
}) {
  return (
    <div className="bg-background text-foreground">
      <main className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <article className="space-y-10">
          <header className="space-y-6 rounded-3xl border border-border/60 bg-card/60 p-6 md:p-8">
            <Breadcrumbs items={breadcrumbItems} />
            <div className="space-y-4">
              <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
                {page.eyebrow}
              </Badge>
              <div className="space-y-4">
                <h1 className="max-w-4xl text-4xl font-semibold tracking-tight md:text-5xl">{page.title}</h1>
                <p className="max-w-3xl text-lg leading-8 text-muted-foreground">{page.summary}</p>
              </div>
            </div>

            {page.slug === 'ai-surfaces' ? <ArchitecturePreviewCard /> : null}
            {page.slug === 'ai-blueprints' ? <BlueprintPreviewCard /> : null}

            <AppScreenshotsGallery title={page.breadcrumbLabel || page.title} screenshots={screenshots} />

            <Separator />

            <section aria-labelledby="quick-links-heading" className="space-y-4">
              <div>
                <h2 id="quick-links-heading" className="text-lg font-semibold text-foreground">
                  Quick links
                </h2>
                <p className="text-sm text-muted-foreground">Move through related MapleOS pages without leaving the semantic content graph.</p>
              </div>
              <ul className="grid gap-3 md:grid-cols-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="group flex h-full items-center justify-between rounded-xl border border-border/60 bg-background/80 px-4 py-3 transition-colors hover:border-primary/40 hover:bg-primary/5"
                    >
                      <span className="font-medium text-foreground">{link.label}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </header>

          <div className="space-y-8">
            {page.sections.map((section) => (
              <SemanticSection key={section.id} section={section} />
            ))}
          </div>

          <FaqSection items={page.faq} />

          <RelatedLinksSection
            title="Related MapleOS pages"
            description="Each page in this content layer links into nearby explainers, app docs, concept docs, and use-case pages to keep the graph crawlable."
            links={relatedLinks}
          />
        </article>
      </main>
    </div>
  );
}